import { Link } from "react-router-dom";
import { Download, ArrowRight, FileText } from "lucide-react";
import CTAButtons from "@/components/CTAButtons";
import TrustBar from "@/components/TrustBar";
import { checklists } from "@/data/checklists";

const TYPE_LABEL = {
    printable: "Printable PDF",
    download: "PDF Download",
    interactive: "Interactive Tool",
};

export default function Checklists() {
    return (
        <div data-testid="page-checklists">
            {/* Hero */}
            <section className="bg-white border-b border-tca-line relative overflow-hidden">
                <div className="absolute inset-0 grid-bg opacity-60" />
                <div className="container-tca relative py-20 md:py-24">
                    <div className="max-w-3xl animate-fade-up">
                        <p className="overline text-tca-orange mb-4">Checklists · Established 2015 · CA(SA) · RA</p>
                        <h1 className="font-display font-extrabold text-tca-ink text-5xl sm:text-6xl lg:text-7xl leading-[0.98]">
                            Tools the team uses
                            <span className="block text-tca-orange">— now in your hands.</span>
                        </h1>
                        <p className="mt-6 text-tca-mute text-lg leading-relaxed max-w-2xl">
                            Free downloads and short interactive questionnaires to help South African business owners get year-end ready, test their tax status, and decide on VAT registration. Built with TC Auditors' technical experience in plain English.
                        </p>
                    </div>
                </div>
            </section>

            {/* Grid */}
            <section className="bg-white">
                <div className="container-tca py-16 md:py-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-tca-line border border-tca-line" data-testid="checklists-grid">
                        {checklists.map((c, i) => {
                            const Icon = c.icon;
                            const isExternal = c.type === "download";
                            const tag = TYPE_LABEL[c.type];
                            const isAlt = i % 4 === 1 || i % 4 === 2;
                            return (
                                <article
                                    key={c.id}
                                    data-testid={`checklist-card-${c.id}`}
                                    className={`p-8 lg:p-10 lift group flex flex-col transition-colors duration-200 ${isAlt ? "bg-orange-100" : "bg-white"} hover:bg-tca-orange`}
                                >
                                    <div className="flex items-start justify-between mb-6">
                                        <div className="w-12 h-12 bg-tca-orangeSoft group-hover:bg-white/20 text-tca-orange group-hover:text-white flex items-center justify-center transition-colors duration-200">
                                            <Icon className="w-6 h-6" />
                                        </div>
                                        <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-tca-mute group-hover:text-white/70 border border-tca-line group-hover:border-white/30 px-2.5 py-1 transition-colors duration-200">
                                            {tag}
                                        </span>
                                    </div>

                                    <p className="overline mb-2 group-hover:text-white/60 transition-colors duration-200">{c.category}</p>
                                    <h3 className="font-display font-bold text-2xl text-tca-ink group-hover:text-white mb-3 transition-colors duration-200">
                                        {c.title}
                                    </h3>
                                    <p className="text-sm text-tca-mute group-hover:text-white/80 leading-relaxed flex-1 transition-colors duration-200">{c.blurb}</p>

                                    <div className="mt-7 pt-5 border-t border-tca-line group-hover:border-white/30 flex items-center justify-between gap-3 transition-colors duration-200">
                                        <span className="text-xs text-tca-mute group-hover:text-white/60 inline-flex items-center gap-1.5 transition-colors duration-200">
                                            <FileText className="w-3.5 h-3.5" /> {c.time}
                                        </span>
                                        {isExternal ? (
                                            <a
                                                href={c.href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                download
                                                data-testid={`checklist-cta-${c.id}`}
                                                className="btn-ghost group-hover:text-white group-hover:border-white/60 transition-colors duration-200"
                                            >
                                                <Download className="w-4 h-4" />
                                                {c.cta}
                                            </a>
                                        ) : (
                                            <Link
                                                to={c.href}
                                                data-testid={`checklist-cta-${c.id}`}
                                                className="btn-ghost group-hover:text-white group-hover:border-white/60 transition-colors duration-200"
                                            >
                                                {c.cta}
                                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                                            </Link>
                                        )}
                                    </div>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            <TrustBar />

            {/* CTA */}
            <section className="bg-tca-fog border-t border-tca-line">
                <div className="container-tca py-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-tca-ink leading-[1.05]">Need the same tools applied to your business?</h2>
                        <p className="mt-4 text-tca-mute">These checklists give you the principle. A 30-minute meeting with TC Auditors gives you the answer for your specific facts.</p>
                    </div>
                    <CTAButtons testIdPrefix="checklists-cta" />
                </div>
            </section>
        </div>
    );
}
